<template>
  <figure
    v-if="content"
    ref="figureEl"
    class="picture"
    :class="{
      'is-loaded': isLoaded,
      'is-portrait': content.height > content.width,
    }"
  >
    <div class="picture__img">
      <img
        ref="imgEl"
        v-src="content"
        :alt="alt"
        :src="content.src"
        :sets="sets"
        :sizes="sizeData"
        :width="content.width"
        :height="content.height"
        :fetchpriority="fetchpriority"
        :loading="loading"
        @load="onLoaded"
      />
    </div>

    <figcaption
      v-if="caption"
      class="picture__caption"
      v-html="caption"
    ></figcaption>
  </figure>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

import { defaultSets } from '@/directives/src'
import { useAppear } from '@/utils/aware'

import type { PropType } from 'vue'
import type { Picture } from '@/types'

const props = defineProps({
  content: {
    type: Object as PropType<Picture>,
    required: true,
  },
  caption: {
    type: String,
    required: false,
    default: undefined,
  },
  alt: {
    type: String,
    required: false,
    default: '',
  },
  sizes: {
    type: String,
    required: false,
    default: '100vw',
  },
  sets: {
    type: Array as PropType<String[]>,
    required: false,
    default: () => defaultSets,
  },
  deferredLoading: {
    type: Number,
    default: 0,
  },
  fetchpriority: {
    type: String as PropType<'auto' | 'low' | 'high'>,
    required: false,
    default: 'auto',
  },
  loading: {
    type: String as PropType<'lazy' | 'eager'>,
    required: false,
    default: 'lazy',
  },
})

// Refs
const figureEl = ref<HTMLElement | null>(null)
const imgEl = ref<HTMLImageElement | null>(null)
const isLoaded = ref(false)
const sizeData = ref('100px')

const onAppear = ({ isInViewport }: { isInViewport: boolean }) => {
  if (isInViewport && props.loading === 'eager') {
    console.log('[onAppear]')
    onLoaded()
  }
}

const onLoaded = () => {
  sizeData.value = props.sizes
  isLoaded.value = true
}

useAppear(figureEl, onAppear, { once: true })

onMounted(() => {
  if (props.loading !== 'eager') {
    setTimeout(() => {
      onLoaded()
    }, props.deferredLoading)
  }
})
</script>

<style lang="scss" scoped>
.picture {
  position: relative;
  overflow: hidden;

  @include mq($until: l) {
    max-width: 100%;
  }
}

.picture__img {
  width: 100%;
  max-width: 100%;
  height: 100%;
  margin: 0 auto;

  img {
    width: 100%;
    opacity: 0;
    transition: opacity 0.25s;
    filter: blur(3px);

    .is-loaded & {
      opacity: 1;
      filter: none;
    }

    .picture.cover & {
      @include image-fit;
    }
  }
}

.picture__caption {
  display: block;
}
</style>
